/**
 * The one hand-written file in the api/ call path — src/api/generated/ is the GENERATED half
 * (LLD-SPA-003); this is the thin instantiation openapi-fetch itself requires, wired to the home
 * API base URL boot/config.ts resolved. Nothing here is contract-derived, so nothing here is
 * regenerated by `npm run generate:api`.
 *
 * `initApiClient` is called exactly once, from main.tsx, after the boot config fetch resolves —
 * never before, because there is no default base URL to fall back to (LLD-SPA-004: no
 * environment value may be baked into the bundle, including "http://localhost").
 */
import createClient from 'openapi-fetch'
import type { Client, Middleware } from 'openapi-fetch'
import type { paths, components } from '@/api/generated/schema'
import { getAccessToken, setAccessToken } from '@/data/store'
import { notifySessionEnded } from '@/data/sessionEvents'
import { notifyRateLimitWaiting } from '@/data/rateLimitEvents'
import { getBootConfiguration } from '@/data/bootConfig'

export type Problem = components['schemas']['Problem']

export class ApiProblemError extends Error {
  readonly problem: Problem
  constructor(problem: Problem) {
    super(problem.title)
    this.problem = problem
  }
}

/**
 * §3.1e/§3.1j's `405` row (OI-C50): a WAF rate-based rule took its `CAPTCHA` action against
 * `/auth/sign-in`, `/auth/sign-up` or `/auth/password/forgot`. Those three are `fetch()` calls, so
 * the browser never renders WAF's own challenge interstitial — the SPA gets a `405` whose body is
 * not JSON and carries no message. **The real puzzle-and-retry UI is not built** (the AWS WAF
 * CAPTCHA JavaScript API surface is undocumented anywhere in this repo, and guessing at a
 * security-relevant third-party API is worse than not building it — `OPEN-ITEMS.md` `OI-C50`).
 * This is the interim, honest fallback Sanket asked for 2026-09-25: a caller who trips the rule
 * sees a plain "too many attempts" message instead of a silently-broken form (a blank error box,
 * previously, from `error` being un-parseable and neither a `Problem`'s `title` nor `refusal_kind`
 * existing on it). No `RefusalKind` member fits this — that enum is backend-owned and append-only
 * (§9.e), and `RATE_LIMITED` already means something more specific (a server-stated
 * `retry_after_seconds`, §3.1j's `429` row) that a WAF `405` does not carry.
 */
export class WafChallengeError extends Error {
  constructor() {
    super('A WAF CAPTCHA challenge was issued and this client has no UI to answer it yet (OI-C50)')
  }
}

let client: Client<paths> | undefined

/** LLD-SPA-171: no module decodes the session token — this middleware only ever reads the
 * opaque string `data/store.ts` holds and attaches it as a bearer credential. */
const attachAccessToken: Middleware = {
  async onRequest({ request }) {
    const token = getAccessToken()
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    return request
  },
}

type RefreshOutcome = 'refreshed' | 'failed'

let inFlightRefresh: Promise<RefreshOutcome> | null = null

/** The queue's deadline is the server's own rotation grace period, served at boot
 * (`session.refresh_grace_seconds`, `data/bootConfig.ts`) — never a client-invented number, per
 * §3.1j's own reasoning: a client constant duplicating a server property is two copies with
 * drifting verdicts. 10s is only the pre-boot fallback, for the vanishingly small window before
 * `loadBootConfiguration()` has resolved. */
function refreshGraceMs(): number {
  const seconds = getBootConfiguration()?.session.refresh_grace_seconds
  return (seconds && seconds > 0 ? seconds : 10) * 1000
}

/** §3.1j's single-flight refresh: one `POST /auth/refresh` per tab, ever, while anything is
 * waiting on it — every other caller awaits this SAME promise rather than starting its own.
 * Bounded by `refreshGraceMs()`: a caller still waiting past the deadline gets `'failed'` even if
 * the network call itself is still pending, so a hung refresh cannot hang the whole queue. */
function runSingleFlightRefresh(): Promise<RefreshOutcome> {
  if (!inFlightRefresh) {
    let timedOut = false
    const attempt: Promise<RefreshOutcome> = getApiClient()
      .POST('/auth/refresh', {})
      .then((result): RefreshOutcome => {
        if (result.error || timedOut) return 'failed'
        setAccessToken(result.data.access_token)
        return 'refreshed'
      })
      .catch((): RefreshOutcome => 'failed')

    const timeout = new Promise<RefreshOutcome>((resolve) => {
      const timer = setTimeout(() => {
        timedOut = true
        resolve('failed')
      }, refreshGraceMs())
      void attempt.finally(() => clearTimeout(timer))
    })

    inFlightRefresh = Promise.race([attempt, timeout]).finally(() => {
      inFlightRefresh = null
    })
  }
  return inFlightRefresh
}

/** A `Request`'s body can only be cloned before it is read, and `fetch(request)` reads it — so
 * the one chance to keep a replayable copy is `onRequest`, before the real send, stashed against
 * the same `Request` reference `onResponse` will see (`attachAccessToken`'s own `onRequest`
 * mutates and returns the SAME object, so the identity is stable end to end). */
const pendingReplayBodies = new WeakMap<Request, Request>()

/**
 * §3.1j's client contract for the routing table's first two rows (`TOKEN_INVALID`,
 * `SESSION_SUPERSEDED`): single-flight refresh, then replay the ORIGINAL request exactly once
 * with the new token. **Deliberately excludes `NO_IDENTITY`** — that kind means the token is
 * valid and only the `users` row is missing, so it must reach the caller's own handling untouched
 * (`OrgResolver`'s `AFinishreg`) rather than end a session that is not actually dead; a previous
 * version of this file collapsed `NO_IDENTITY` into a sign-out, which was exactly the wrong row.
 *
 * The replay is a raw `fetch`, not a re-entry into this client — it bypasses this whole
 * middleware pipeline, which is what makes "a replayed request is never retried again" true by
 * construction rather than by a flag: a second 401 on it is just returned as a real failure.
 */
const refreshAndReplay: Middleware = {
  async onRequest({ request }) {
    pendingReplayBodies.set(request, request.clone())
    return undefined
  },

  async onResponse({ request, response, options }) {
    if (response.status !== 401 || !request.headers.has('Authorization')) return undefined
    // `/auth/refresh` reads the cookie only and ignores any bearer header it happens to carry
    // (§3.1e) — its OWN 401 means no session exists at all, never "this token expired, refresh".
    if (request.url.endsWith('/auth/refresh')) return undefined

    const problem = await response
      .clone()
      .json()
      .catch(() => undefined)
    if (problem?.refusal_kind !== 'TOKEN_INVALID' && problem?.refusal_kind !== 'SESSION_SUPERSEDED') return undefined

    const outcome = await runSingleFlightRefresh()
    if (outcome === 'failed') {
      notifySessionEnded()
      return undefined
    }

    const replayFrom = pendingReplayBodies.get(request)
    if (!replayFrom) return undefined
    const replay = new Request(replayFrom, { headers: new Headers(replayFrom.headers) })
    replay.headers.set('Authorization', `Bearer ${getAccessToken()}`)
    return options.fetch(replay)
  },
}

/**
 * §3.1j's client contract for the `429 + Retry-After` row: **wait the header, then one retry** —
 * never immediately, never an invented exponential guess when the server already gave a number
 * (OI-C49). Reuses `refreshAndReplay`'s own `pendingReplayBodies` stash rather than a second
 * `WeakMap` keyed on the same `Request` — that middleware's `onRequest` already clones every
 * request unconditionally, and the identity is stable through the whole pipeline (see its own
 * doc comment). The replay is the same raw `options.fetch(replay)` bypass, so "never retried
 * again" holds here for the same reason it holds there: a second 429 on the replay is a real
 * failure, not a second wait.
 */
const waitAndRetryOn429: Middleware = {
  async onResponse({ request, response, options }) {
    if (response.status !== 429) return undefined
    const retryAfterHeader = response.headers.get('Retry-After')
    // No header, or not a plain number of seconds — nothing safe to wait for, so this falls
    // through to a normal error rather than guessing an interval the server never gave.
    // (`Number(null)` is 0, not `NaN` — the explicit null check is load-bearing, not redundant.)
    if (retryAfterHeader === null) return undefined
    const retryAfterSeconds = Number(retryAfterHeader)
    if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds < 0) return undefined

    const replayFrom = pendingReplayBodies.get(request)
    if (!replayFrom) return undefined

    notifyRateLimitWaiting(true)
    try {
      await new Promise((resolve) => setTimeout(resolve, retryAfterSeconds * 1000))
    } finally {
      notifyRateLimitWaiting(false)
    }

    const replay = new Request(replayFrom, { headers: new Headers(replayFrom.headers) })
    return options.fetch(replay)
  },
}

export function initApiClient(baseUrl: string): void {
  // The refresh cookie is `SameSite=Lax`/host-only on `api.{domain}`, a different origin than the
  // CloudFront-served SPA (`design/03-auth-and-credentials.md` §3.1e's CSRF posture) — without
  // `credentials: 'include'`, the browser neither sends it on `/auth/refresh` nor stores it from
  // `/auth/sign-in`'s `Set-Cookie`, so the whole refresh-cookie mechanism is inert cross-origin.
  client = createClient<paths>({ baseUrl, credentials: 'include' })
  client.use(attachAccessToken)
  client.use(refreshAndReplay)
  client.use(waitAndRetryOn429)
}

export function getApiClient(): Client<paths> {
  if (!client) {
    throw new Error('getApiClient() called before initApiClient() — boot/config.ts must resolve first')
  }
  return client
}

/** Every mutation in this app goes through this one call shape: throw `ApiProblemError` on any
 * non-2xx response (the envelope is always `application/problem+json` — api/openapi.yaml's
 * info.description, "Errors, refusals and the correlation identifier") rather than returning a
 * `{ data, error }` pair a caller might forget to check. */
export function unwrap<T>({ data, error }: { data?: T; error?: Problem }): T {
  if (error !== undefined) {
    throw new ApiProblemError(error)
  }
  if (data === undefined) {
    throw new Error('Response had neither data nor error — this should be unreachable')
  }
  return data
}
